import React, {
	Dispatch,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
	Icon,
	InputGroup,
	MultilineTextBox,
	RadioButton,
	Select,
	theme,
} from "uikit";

import {
	ClientRefusalReason,
	PhoneBlacklist,
} from "../../../../../../../../services";
import { useTypedSelector } from "../../../../../../../../redux/store";
import useObjectEditor from "../../../../../../../../hooks/useObjectEditor";
import { getClientRefusalReasonName } from "../../../../../../../../utils/clientRefusalReason";
import Divider from "../../../../../../../../components/Divider";
import {
	SearchIconBorders,
	SearchTextBox,
} from "../../../../../../../../components";

import Root from "./components/Root";

const tPath = "pages.mainPage.pages.orders.clientCancelOrderModal.content";

const List = styled.div`
	flex: 1 1 auto;
	min-height: 120px;
	overflow-y: auto;
`;

const Reason = styled.div<{ highlighted: boolean }>`
	padding: 8px;
	border-radius: 4px;
	cursor: pointer;
	background: ${({ highlighted }) =>
		highlighted ? theme.colors.button_secondary : "transparent"};
`;

const Message = styled.div<{ error?: boolean }>`
	padding: 8px;
	font-size: 13px;
	color: ${({ error }) =>
		error ? theme.colors.negative : theme.colors.disabled_text};
`;

const Content: React.FC<Content.Props> = ({
	value,
	reasons,
	highlightedReason,
	addClientToBlackList,
	errorComment,
	errorReason,
	onChange,
	onChangeHighlightedReason,
}) => {
	const { t } = useTranslation();
	const language = useTypedSelector((state) => state.session.language);
	const valueEditor = useObjectEditor(value, onChange);

	const [query, setQuery] = useState("");

	const searchRef = useRef<HTMLDivElement | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);

	const reasonValue = valueEditor.useGetter("reason");
	const reasonOnChange = valueEditor.useSetter("reason");

	const commentValue = valueEditor.useGetter("comment");
	const commentOnChange = valueEditor.useSetter("comment");

	const blockLevelValue = valueEditor.useGetter("blockLevel");
	const blockLevelOnChange = valueEditor.useSetter("blockLevel");

	const blockCommentValue = valueEditor.useGetter("blockComment");
	const blockCommentOnChange = valueEditor.useSetter("blockComment");

	const options = useMemo(() => {
		const search = query.trim().toLocaleLowerCase();

		return reasons
			.map((reason) => ({
				id: reason.id,
				label: getClientRefusalReasonName(reason.name, language),
			}))
			.filter(({ label }) => label.toLocaleLowerCase().includes(search));
	}, [language, query, reasons]);

	useEffect(() => {
		searchRef.current?.querySelector("input")?.focus();
	}, []);

	const moveHighlight = useCallback(
		(step: 1 | -1) => {
			if (!options.length) return;

			const index = options.findIndex(
				({ id }) => id === highlightedReason,
			);
			const fromStart = step === 1 ? 0 : options.length - 1;
			const next =
				index === -1
					? fromStart
					: (index + step + options.length) % options.length;

			onChangeHighlightedReason(options[next].id);
		},
		[highlightedReason, onChangeHighlightedReason, options],
	);

	// На window в фазе перехвата: иначе стрелки двигают таблицу заказов под
	// модалкой. Поля комментария и блокировки клавиши обрабатывают сами.
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target as Node;
			const isOwnTarget =
				target === document.body ||
				searchRef.current?.contains(target) ||
				listRef.current?.contains(target);

			if (!isOwnTarget) return;

			if (event.key === "ArrowDown" || event.key === "ArrowUp") {
				moveHighlight(event.key === "ArrowDown" ? 1 : -1);
			} else if (event.code === "Space" && highlightedReason !== null) {
				// Пока строка не подсвечена, пробел печатается в поиск: в
				// названиях причин есть пробелы.
				reasonOnChange(highlightedReason);
			} else {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
		};

		window.addEventListener("keydown", handleKeyDown, true);
		return () => window.removeEventListener("keydown", handleKeyDown, true);
	}, [highlightedReason, moveHighlight, reasonOnChange]);

	useEffect(() => {
		if (highlightedReason === null) return;

		listRef.current
			?.querySelector(`[data-reason-id="${highlightedReason}"]`)
			?.scrollIntoView({ block: "nearest" });
	}, [highlightedReason]);

	const clientBlockOptions = useMemo(
		() => [
			{
				key: PhoneBlacklist.Level.WARNING,
				label: t(`${tPath}.str200`) ?? "",
				value: PhoneBlacklist.Level.WARNING,
			},
			{
				key: PhoneBlacklist.Level.BLOCKED,
				label: t(`${tPath}.str201`) ?? "",
				value: PhoneBlacklist.Level.BLOCKED,
			},
			{
				key: PhoneBlacklist.Level.BAN_CALLS,
				label: t(`${tPath}.str202`) ?? "",
				value: PhoneBlacklist.Level.BAN_CALLS,
			},
		],
		[t],
	);

	return (
		<Root>
			<div ref={searchRef}>
				<InputGroup.InputGroup sizes="32px auto">
					<SearchIconBorders>
						<Icon
							id="search"
							size={16}
							colors={[theme.colors.disabled_text]}
						/>
					</SearchIconBorders>
					<SearchTextBox
						style={{ width: "100%" }}
						value={query}
						placeholder={t(`${tPath}.search`) ?? ""}
						onChange={(next) => {
							setQuery(next);
							onChangeHighlightedReason(null);
						}}
					/>
				</InputGroup.InputGroup>
			</div>
			<List ref={listRef}>
				{options.map(({ id, label }) => (
					<Reason
						key={id}
						data-reason-id={id}
						highlighted={highlightedReason === id}
						onClick={() => {
							reasonOnChange(id);
							onChangeHighlightedReason(id);
						}}
					>
						<RadioButton.RadioButton
							selected={reasonValue === id}
							label={label}
						/>
					</Reason>
				))}
				{!options.length && (
					<Message>{t(`${tPath}.notFound`) ?? ""}</Message>
				)}
			</List>
			{errorReason && (
				<Message error>{t(`${tPath}.reasonRequired`) ?? ""}</Message>
			)}
			<MultilineTextBox
				value={commentValue}
				style={{ height: 80, flex: "none" }}
				placeholder={t(`${tPath}.str0`) ?? ""}
				onChange={commentOnChange}
			/>
			{addClientToBlackList && (
				<>
					<Divider
						orientation="horizontal"
						size="max"
						color="#DEE0E2"
					/>
					<Select
						value={blockLevelValue}
						options={clientBlockOptions}
						onSelect={(option) => blockLevelOnChange(option.value)}
					/>
					<MultilineTextBox
						error={errorComment}
						value={blockCommentValue}
						style={{ height: 80, flex: "none" }}
						placeholder={t(`${tPath}.str1`) ?? ""}
						onChange={blockCommentOnChange}
					/>
				</>
			)}
		</Root>
	);
};

declare namespace Content {
	interface Value {
		/** Id причины из справочника. */
		reason?: number;
		comment: string;
		blockLevel: PhoneBlacklist.Level;
		blockComment?: string;
	}

	interface Props {
		value: Value;
		reasons: ClientRefusalReason.Model[];
		highlightedReason: number | null;

		addClientToBlackList: boolean;
		errorComment: boolean;
		errorReason: boolean;

		onChange: Dispatch<Value>;
		onChangeHighlightedReason: Dispatch<number | null>;
	}
}

export default Content;
